<template>
  <!-- Video -->
  <div
    v-if="bloque.id_tipo_de_bloque === 'video'"
    class="item-video link-item-list-preview"
    @click="registraEvento(bloque, 'click_bloque')"
  >
    <!-- YouTube video -->
    <div v-if="isYouTubeVideo" :class="aspectRatioClass">
      <iframe
        :src="cleanYouTubeUrl"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      ></iframe>
    </div>

    <!-- Miqro hosted video -->
    <div v-else-if="isMiqroVideo" :class="aspectRatioClass">
      <video
        ref="videoElement"
        controls
        loop
        playsinline
        webkit-playsinline
        preload="metadata"
        @loadedmetadata="onVideoMetadataLoaded"
        @error="onVideoError"
        @click="playVideo"
        @loadeddata="onVideoLoaded"
        @canplay="onVideoCanPlay"
      >
        <source :src="videoSource" :type="videoMimeType">
        Tu navegador no soporta videos HTML5.
      </video>
    </div>

    <!-- Other videos (fallback) -->
    <iframe
      v-else
      type="text/html"
      :src="videoSource"
      frameborder="0"
    ></iframe>
  </div>
</template>

<script>
import crudBloques from "../../../js/crud-bloques";

export default {
  props: {
    bloque: Object,
  },
  data() {
    return {
      videoAspectRatio: '16:9', // Default aspect ratio
    };
  },
  computed: {
    videoSource() {
      return this.bloque.video_archivo_url || this.bloque.video_url;
    },
    videoMimeType() {
      const url = this.videoSource;
      if (!url) return 'video/mp4';

      const extension = url.split('.').pop().toLowerCase();
      switch (extension) {
        case 'mp4':
          return 'video/mp4';
        case 'webm':
          return 'video/webm';
        case 'ogg':
          return 'video/ogg';
        case 'mov':
          return 'video/quicktime';
        case 'avi':
          return 'video/x-msvideo';
        default:
          return 'video/mp4';
      }
    },
    isYouTubeVideo() {
      const url = this.videoSource;
      if (!url) return false;
      return url.includes('youtube.com') || url.includes('youtu.be');
    },
    isMiqroVideo() {
      const url = this.videoSource;
      if (!url) return false;
      return url.includes('admin.miqro.app/files') || (
        process.env.R2_PUBLIC_URL && url.startsWith(process.env.R2_PUBLIC_URL)
      );
    },
    cleanYouTubeUrl() {
      if (!this.isYouTubeVideo) return this.videoSource;

      let url = this.videoSource;

      // Convert regular YouTube URLs to embed format
      if (url.includes('youtube.com/watch?v=')) {
        const videoId = url.split('v=')[1].split('&')[0];
        url = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1].split('?')[0];
        url = `https://www.youtube.com/embed/${videoId}`;
      }

      // Add parameters to remove controls and branding
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3&disablekb=1`;
    },
    aspectRatioClass() {
      return this.videoAspectRatio === '9:16' ? 'aspect-ratio-9-16' : 'aspect-ratio-16-9';
    }
  },
  methods: {
    registraEvento(bloque, evento, redirect = false, redirectUrl = "") {
      const self = this;
      crudBloques.registraEvento(self, bloque, evento, redirect, redirectUrl);
    },
    onVideoMetadataLoaded() {
      console.log('Video metadata loaded:', this.videoSource);
      if (this.$refs.videoElement) {
        const video = this.$refs.videoElement;

        // Sometimes dimensions aren't available immediately, try with a delay
        setTimeout(() => {
          const width = video.videoWidth;
          const height = video.videoHeight;

          console.log('Video dimensions:', width, 'x', height);
          console.log('Video readyState:', video.readyState);
          console.log('Video networkState:', video.networkState);

          if (width && height) {
            const aspectRatio = width / height;

            // Determine if it's portrait (9:16) or landscape (16:9)
            if (aspectRatio < 1) {
              // Portrait video (height > width)
              this.videoAspectRatio = '9:16';
            } else {
              // Landscape video (width > height)
              this.videoAspectRatio = '16:9';
            }
          } else {
            console.warn('Video dimensions still not available, using default 16:9');
            this.videoAspectRatio = '16:9';
          }
        }, 100);
      }
    },
    onVideoError(event) {
      console.error('Video error:', event, this.videoSource);
    },
    playVideo() {
      if (this.$refs.videoElement) {
        const video = this.$refs.videoElement;

        // Check dimensions when user tries to play
        const width = video.videoWidth;
        const height = video.videoHeight;

        if (width === 0 && height === 0) {
          console.log('Dimensions still 0x0, forcing reload');
          video.load();

          // Wait for reload and try again
          setTimeout(() => {
            this.forceDimensionCheck();
          }, 500);
        }

        video.play().catch(error => {
          console.log('Video play failed:', error);
        });
      }
    },
    onVideoLoaded() {
      console.log('Video data loaded');
      if (this.$refs.videoElement) {
        const video = this.$refs.videoElement;
        video.currentTime = 0.1;

        // Double check dimensions here too
        setTimeout(() => {
          const width = video.videoWidth;
          const height = video.videoHeight;
          console.log('Video dimensions on loadeddata:', width, 'x', height);

          if (width && height && (this.videoAspectRatio === '16:9')) {
            const aspectRatio = width / height;
            if (aspectRatio < 1) {
              this.videoAspectRatio = '9:16';
            } else {
              this.videoAspectRatio = '16:9';
            }
            console.log('Updated aspect ratio to:', this.videoAspectRatio);
          }
        }, 200);
      }
    },
    onVideoCanPlay() {
      console.log('Video can play');
      if (this.$refs.videoElement) {
        const video = this.$refs.videoElement;

        // Final check for dimensions
        setTimeout(() => {
          const width = video.videoWidth;
          const height = video.videoHeight;
          console.log('Video dimensions on canplay:', width, 'x', height);

          if (width && height) {
            const aspectRatio = width / height;
            const newAspectRatio = aspectRatio < 1 ? '9:16' : '16:9';

            if (this.videoAspectRatio !== newAspectRatio) {
              this.videoAspectRatio = newAspectRatio;
              console.log('Final aspect ratio set to:', this.videoAspectRatio);
            }
          }
        }, 300);
      }
    },
    async detectYouTubeAspectRatio() {
      // For YouTube videos, we can try to get video info via YouTube API
      // or use oembed API, but this requires API keys
      // For now, we'll assume 16:9 for YouTube videos
      // You could extend this to use YouTube Data API v3

      if (this.isYouTubeVideo) {
        try {
          const videoId = this.extractYouTubeVideoId();
          if (videoId) {
            // You could make an API call here to get video dimensions
            // For now, keeping default 16:9
            this.videoAspectRatio = '16:9';
          }
        } catch (error) {
          console.log('Could not detect YouTube video aspect ratio');
          this.videoAspectRatio = '16:9';
        }
      }
    },
    extractYouTubeVideoId() {
      const url = this.videoSource;
      if (!url) return null;

      let videoId = null;
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1].split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      }

      return videoId;
    },
    forceDimensionCheck() {
      const video = this.$refs.videoElement;
      if (!video) return;

      console.log('Force checking video dimensions');

      const checkDimensions = () => {
        const width = video.videoWidth;
        const height = video.videoHeight;

        console.log('Force check - Video dimensions:', width, 'x', height);
        console.log('Force check - Video readyState:', video.readyState);
        console.log('Force check - Video src:', video.src);

        if (width && height) {
          const aspectRatio = width / height;
          const newAspectRatio = aspectRatio < 1 ? '9:16' : '16:9';
          this.videoAspectRatio = newAspectRatio;
          console.log('Force check - Aspect ratio set to:', this.videoAspectRatio);
          return true;
        }
        return false;
      };

      // Try immediately
      if (checkDimensions()) return;

      // Alternative approach: Create temporary video element
      this.createTemporaryVideo();

      // Try with intervals if not available yet
      let attempts = 0;
      const maxAttempts = 20;
      const interval = setInterval(() => {
        attempts++;

        if (checkDimensions() || attempts >= maxAttempts) {
          clearInterval(interval);
          if (attempts >= maxAttempts) {
            console.warn('Could not get video dimensions after', maxAttempts, 'attempts');
            // Fallback: try to load video programmatically
            video.load();
          }
        }
      }, 250);
    },
    createTemporaryVideo() {
      console.log('Creating temporary video element for dimension detection');

      const tempVideo = document.createElement('video');
      tempVideo.style.position = 'absolute';
      tempVideo.style.visibility = 'hidden';
      tempVideo.style.width = '1px';
      tempVideo.style.height = '1px';
      tempVideo.src = this.videoSource;
      tempVideo.preload = 'metadata';

      const onTempVideoLoad = () => {
        const width = tempVideo.videoWidth;
        const height = tempVideo.videoHeight;

        console.log('Temp video dimensions:', width, 'x', height);

        if (width && height) {
          const aspectRatio = width / height;
          const newAspectRatio = aspectRatio < 1 ? '9:16' : '16:9';
          this.videoAspectRatio = newAspectRatio;
          console.log('Temp video - Aspect ratio set to:', this.videoAspectRatio);
        }

        // Clean up
        document.body.removeChild(tempVideo);
      };

      tempVideo.addEventListener('loadedmetadata', onTempVideoLoad);
      tempVideo.addEventListener('error', () => {
        console.warn('Temp video failed to load');
        document.body.removeChild(tempVideo);
      });

      document.body.appendChild(tempVideo);
    },
  },
  mounted() {
    // Try to detect aspect ratio for YouTube videos
    if (this.isYouTubeVideo) {
      this.detectYouTubeAspectRatio();
    }

    // Force check dimensions after component mount
    this.$nextTick(() => {
      if (this.isMiqroVideo && this.$refs.videoElement) {
        this.forceDimensionCheck();
      }
    });
  },
};
</script>

<style scoped>
.aspect-ratio-16-9 {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
}

.aspect-ratio-16-9 iframe,
.aspect-ratio-16-9 video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 10px;
  background: #000;
}

.aspect-ratio-9-16 {
  position: relative;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding-bottom: 177.78%; /* 9:16 aspect ratio */
  height: 0;
  overflow: hidden;
}

.aspect-ratio-9-16 iframe,
.aspect-ratio-9-16 video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 10px;
  background: #000;
}
</style>
